import { ReactNode, useEffect, useMemo } from 'react';

import { AssetDTO, GetUnitsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import excavatorVector from '@/assets/vectors/excavator.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { usePermissions } from '@/hooks/use-permissions';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createAssetTableColumns } from './table-meta';

export function AssetsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  units,
}: {
  notFoundDisplay: ReactNode;
  data: AssetDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  units: GetUnitsResponseDTO['data'];
}) {
  const navigate = useWorkspaceNavigate();
  const { syncModuleColumns } = useModulesStore();
  const { canManageAssets } = usePermissions();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.Assets, createAssetTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<AssetDTO>
        data={data}
        columns={createAssetTableColumns({ units, canManageAssets })}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={(row) => navigate(`/assets/${row.id}`)}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoAssetsCreatedDisplay({ openAssetDrawer, openBulkImportDialog }: { openAssetDrawer: () => void; openBulkImportDialog: () => void }) {
  const { canManageAssets } = usePermissions();
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={excavatorVector} alt="Excavator" className="h-[114.5px] w-[220px]" />
      <span className="text-xl leading-[30px] font-medium">No assets have been created yet</span>
      {canManageAssets && (
        <>
          <div className="flex flex-col gap-0 text-center">
            <span className="text-secondary-text text-sm">
              You can add in your assets manually or bulk import by uploading an Excel file using our template.
            </span>
          </div>
          <div className="pointer-events-auto flex gap-1">
            <Button variant="success" onClick={openAssetDrawer}>
              Create Asset
            </Button>
            <Button variant="grey" onClick={openBulkImportDialog}>
              Bulk upload assets
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function NoAssetsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No assets match your search</span>
    </div>
  );
}

export function NoAssetsFoundFromFiltersDisplay({ openAssetDrawer, removeColumnFilters }: { openAssetDrawer: () => void; removeColumnFilters: () => void }) {
  const { canManageAssets } = usePermissions();
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No assets match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters{canManageAssets ? ' or create a new asset' : ''}.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
        {canManageAssets && (
          <Button variant="success" onClick={openAssetDrawer}>
            Create Asset
          </Button>
        )}
      </div>
    </div>
  );
}
